<template>
  <div
    v-editable="blok"
    class="MarketsWidget"
    :class="[desktopWidth('lg'), blockPaddingTop('md'), blockPaddingBottom('md')]"
  >
    <div class="layout-container">
      <div class="flex flex-col desk:flex-row gap-32 desk:gap-64">
        <!-- Left side: Info -->
        <div class="desk:basis-1/2">
          <div v-if="blok.label" class="type-tagline-xs text-text-subtle mb-12">
            {{ blok.label }}
          </div>
          <h2
            v-if="blok.title"
            class="type-headline-xl desk:type-headline-2xl mb-16"
            :class="{ 'wysiwyg': typeof blok.title !== 'string' }"
          >
            <template v-if="typeof blok.title === 'string'">{{ blok.title }}</template>
            <span v-else v-html="renderRichText(blok.title)" />
          </h2>
          <div
            v-if="blok.body?.content?.length"
            class="type-base text-text-subtle mb-32 wysiwyg"
            v-html="renderRichText(blok.body)"
          />

          <div class="border-b border-border-base mb-32" />

          <div class="flex flex-row items-start gap-32 mb-32">
            <div>
              <div class="type-headline-2xl desk:type-headline-3xl">{{ totalMarkets }}</div>
              <div class="type-tagline-xs text-text-subtle">{{ $t('marketsWidget.markets') }}</div>
            </div>
            <div>
              <div class="type-headline-2xl desk:type-headline-3xl">{{ totalRegions }}</div>
              <div class="type-tagline-xs text-text-subtle">{{ $t('marketsWidget.regions') }}</div>
            </div>
          </div>

          <NuxtLink
            v-if="blok.buttonText"
            :to="$sbLinkFix(blok.buttonLink)"
            :target="$sbLinkTarget(blok.buttonLink)"
          >
            <div class="btn btn--primary inline-flex items-center gap-8">
              {{ blok.buttonText }}
              <img
                src="/icons/arrow-right-inv.svg"
                class="w-16 h-16"
                alt=""
              >
            </div>
          </NuxtLink>
        </div>

        <!-- Right side: Markets list -->
        <div class="desk:basis-1/2 bg-bg-base border border-border-subtle">
          <!-- Search with count -->
          <div class="mini-search">
            <img
              src="/icons/search.svg"
              alt=""
            >
            <input
              v-model="searchQuery"
              type="text"
              :placeholder="$t('marketsWidget.searchPlaceholder')"
            >
            <span>
              {{ totalMarkets }} {{ $t('marketsWidget.markets') }}
            </span>
          </div>

          <!-- Markets list -->
          <div class="max-h-[500px] overflow-y-auto">
            <div
              v-for="region in filteredRegions"
              :key="region.name"
            >
              <!-- Region header -->
              <div class="sticky top-0 z-10 bg-bg-subtle py-12 px-24 border-t border-b border-border-subtle flex items-baseline justify-between">
                <span class="type-tagline-xs uppercase">{{ region.name }}</span>
                <span class="type-tagline-xs text-text-subtle">{{ String(region.markets.length).padStart(2, '0') }}</span>
              </div>

              <!-- Markets in region -->
              <div>
                <div
                  v-for="market in region.markets"
                  :key="market.uuid"
                  class="border-b border-border-subtle"
                >
                  <!-- Market row -->
                  <div
                    class="py-16 px-24 cursor-pointer select-none flex justify-between items-center hover:bg-bg-subtle transition-colors"
                    :class="{ 'bg-bg-subtle': openMarkets.includes(market.uuid) }"
                    @click="toggleMarket(market.uuid)"
                  >
                    <div class="flex items-center gap-12">
                      <img
                        v-if="market.content.flagSuffix"
                        :src="`/flags/${market.content.flagSuffix}.svg`"
                        :alt="market.name"
                        class="w-28 h-20 object-contain rounded-2 shrink-0"
                      >
                      <div>
                        <div class="font-serif font-medium text-17 leading-tight -tracking-[0.005em]">{{ market.name }}</div>
                        <div v-if="getFirstContactBox(market)?.city" class="type-tagline-xs text-text-subtle italic normal-case">
                          {{ getFirstContactBox(market)?.city }}
                        </div>
                      </div>
                    </div>
                    <img
                      :src="openMarkets.includes(market.uuid) ? '/icons/chevron-up.svg' : '/icons/chevron-down.svg'"
                      class="w-20 h-20 shrink-0"
                      alt=""
                    >
                  </div>

                  <!-- Market details (expanded) -->
                  <transition
                    name="accordion"
                    @enter="enter"
                    @leave="leave"
                  >
                    <div
                      v-show="openMarkets.includes(market.uuid)"
                      class="overflow-hidden"
                    >
                      <div class="pl-64 pr-24 pb-16 bg-bg-subtle">
                        <div v-if="getFirstContactBox(market)" class="flex flex-col gap-8 mb-16">
                          <div v-if="getFirstContactBox(market)?.address" class="flex items-start gap-8">
                            <img
                              src="/icons/location.svg"
                              class="w-16 h-16 mt-2 shrink-0 opacity-60"
                              alt=""
                            >
                            <span class="type-sm">{{ getFirstContactBox(market)?.address }}</span>
                          </div>
                          <div v-if="getFirstContactBox(market)?.phone" class="flex items-center gap-8">
                            <img
                              src="/icons/phone.svg"
                              class="w-16 h-16 shrink-0 opacity-60"
                              alt=""
                            >
                            <a
                              :href="`tel:${getFirstContactBox(market)?.phone}`"
                              class="type-sm"
                            >
                              {{ getFirstContactBox(market)?.phone }}
                            </a>
                          </div>
                          <div v-if="getFirstContactBox(market)?.email" class="flex items-center gap-8">
                            <img
                              src="/icons/mail.svg"
                              class="w-16 h-16 shrink-0 opacity-60"
                              alt=""
                            >
                            <a
                              :href="`mailto:${getFirstContactBox(market)?.email}`"
                              class="type-sm"
                            >
                              {{ getFirstContactBox(market)?.email }}
                            </a>
                          </div>
                        </div>
                        <!-- Shop at local site button -->
                        <NuxtLink
                          v-if="market.content.hasLocalSite && market.content.localSiteLink"
                          :to="$sbLinkFix(market.content.localSiteLink)"
                          class="btn btn--primary inline-flex items-center justify-center gap-8 w-full"
                        >
                          {{ $t('marketsListing.shopAtLocalSite') }}
                        </NuxtLink>
                        <!-- More information link -->
                        <NuxtLink
                          v-else
                          :to="getMarketUrl(market)"
                          class="group inline-flex items-center gap-4 type-xs-medium"
                        >
                          {{ $t('marketsWidget.moreInformation') }}
                          <img
                            src="/icons/arrow-right.svg"
                            class="w-[13px] h-[13px] transition-transform group-hover:translate-x-4"
                            alt=""
                          >
                        </NuxtLink>
                      </div>
                    </div>
                  </transition>
                </div>
              </div>
            </div>

            <div v-if="filteredRegions.length === 0 && searchQuery" class="py-24 px-24 text-center text-text-subtle">
              {{ $t('search.nohits') }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ISbRichtext } from 'storyblok-js-client';
import type { StoryBlokPropBase, StoryBlokLink } from '#constants/types/storyblok';
import type { StoryBlokSingleMarketContent } from '~/storyblok/SingleMarket.vue';
import useVisualAppearancePlugin from '~/composables/useVisualAppearancePlugin';
import { useGlobalContentStore } from '~/store/globalContent';

interface Props extends StoryBlokPropBase {
  label: string;
  title: ISbRichtext | string;
  body: ISbRichtext;
  buttonText: string;
  buttonLink: StoryBlokLink;
  marketsFolder: string;
}

const props = defineProps<{
  blok: Props;
}>();

const { $t, $sbLinkFix, $sbLinkTarget } = useNuxtApp();
const globalContent = useGlobalContentStore();
const storyblokApi = useStoryblokApi();

const { desktopWidth, blockPaddingTop, blockPaddingBottom } = useVisualAppearancePlugin(props);

// State
const searchQuery = ref('');
const openMarkets = ref<string[]>([]);


// Fetch markets data
interface MarketStory {
  uuid: string;
  name: string;
  full_slug: string;
  content: StoryBlokSingleMarketContent;
}

const { data: marketsData } = await useAsyncData(`markets-widget-${props.blok._uid}`, async() => {
  const folder = props.blok.marketsFolder || 'markets';
  const basePath = globalContent.getStoryblokBasePath;
  const startsWithPath = basePath ? `${basePath}/${folder}` : folder;

  const { data } = await storyblokApi.get('cdn/stories/', {
    version: useRuntimeConfig().public.storyblokStoriesVersion as 'draft' | 'published',
    starts_with: startsWithPath,
    content_type: 'SingleMarket',
    per_page: 100,
  });

  return data?.stories || [];
});

const markets = computed<MarketStory[]>(() => marketsData.value || []);

// Group markets by region
interface RegionGroup {
  name: string;
  markets: MarketStory[];
}

const groupedByRegion = computed<RegionGroup[]>(() => {
  const groups: Record<string, MarketStory[]> = {};

  markets.value.forEach((market) => {
    const region = market.content.region || 'Other';
    if (!groups[region]) {
      groups[region] = [];
    }
    groups[region].push(market);
  });

  // Sort regions alphabetically and markets within each region
  return Object.entries(groups)
    .map(([name, markets]) => ({
      name,
      markets: markets.sort((a, b) =>
        a.name.localeCompare(b.name)
      ),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
});

// Filter based on search
const filteredRegions = computed<RegionGroup[]>(() => {
  if (!searchQuery.value.trim()) {
    return groupedByRegion.value;
  }

  const query = searchQuery.value.toLowerCase().trim();

  return groupedByRegion.value
    .map((region) => ({
      ...region,
      markets: region.markets.filter((market) =>
        market.name?.toLowerCase().includes(query) ||
        market.content.region?.toLowerCase().includes(query) ||
        market.content.city?.toLowerCase().includes(query)
      ),
    }))
    .filter((region) => region.markets.length > 0);
});

// Stats
const totalMarkets = computed(() => markets.value.length);
const totalRegions = computed(() => groupedByRegion.value.length);

// Toggle functions
const toggleMarket = (marketId: string) => {
  if (openMarkets.value.includes(marketId)) {
    openMarkets.value = openMarkets.value.filter((m) => m !== marketId);
  } else {
    openMarkets.value.push(marketId);
  }
};

// Get first contact box from market
const getFirstContactBox = (market: MarketStory) => {
  const boxes = market.content.contactBoxes;
  if (!boxes || boxes.length === 0) { return null; }
  return boxes[0] as {
    label?: string;
    title?: string;
    city?: string;
    description?: string;
    address?: string;
    phone?: string;
    email?: string;
    website?: StoryBlokLink;
  };
};

// Get market URL
const getMarketUrl = (market: MarketStory) => {
  const basePath = globalContent.getPrependPathForManualLinks;
  const slug = market.full_slug.replace(globalContent.getStoryblokBasePath, '').replace(/^\//, '');
  return `${basePath}/${slug}`.replace(/\/+/g, '/');
};

// Accordion animation
const enter = (el: HTMLElement) => {
  el.style.maxHeight = el.scrollHeight + 'px';
};

const leave = (el: HTMLElement) => {
  el.style.maxHeight = el.scrollHeight + 'px';
  setTimeout(() => {
    el.style.maxHeight = '0';
  });
};

</script>

<style scoped lang="postcss">
.mini-search {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 24px;
  border-bottom: 1px solid theme('colors.border.subtle');
  flex-shrink: 0;
  background: theme('colors.bg.base');
  font-size: 13px;

  img {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    opacity: 0.6;
  }

  input {
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
  }

  span {
    flex-shrink: 0;
    color: theme('colors.text.subtle');
  }
}

:deep(.wysiwyg p) {
  @apply text-18 leading-26 desk:leading-28 mt-16;
}
</style>
